/** Captured paid product types describe observed buyers, never a complete lifetime assortment. */
import * as z from 'zod';
import { insightAnswerMetadataSchema } from './insight-answer.js';
import { insightCategoryBreakdownSchema } from './insight-category-breakdown.js';
import { buildProductMixPresentation } from './product-mix-presentation.js';
export const productMixDefinition = {
    id: 'product-mix',
    owner: 'shopify',
    basis: 'observed_paid_buyer_captured_product_types',
    question: 'Which product types appear in collected paid purchases?',
    population: 'Distinct identified customers with qualifying paid products in collected orders; anonymous baskets are separate.',
    timeBasis: 'Qualifying basket creation dates, not a rolling window or lifetime coverage; collection times are separate.',
    ordering: 'buyers_desc_exact_label_utf16_asc',
    unsupportedClaims: [
        'Collected paid purchases are not complete lifetime history or a random sample.',
        'Type memberships overlap; shares do not sum to 100%.',
        'Captured product types are not inferred from names or the current catalog.',
        'Missing current-parent children and unknown eligibility are not evidence of no purchases.',
        'Observed type mix is not loyalty, conversion, causal uplift or a forecast.',
    ],
};
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const date = z.iso.datetime({ offset: true }).nullable();
export const productMixCoverageSchema = z.strictObject({
    totalOrders: count,
    eligibleOrders: count,
    excludedOrders: count,
    unknownOrders: count,
    currentLines: count,
    eligibleOrderLines: count,
    eligibleLines: count,
    excludedLines: count,
    unknownLines: count,
    eligibleOrdersWithoutLines: count,
    qualifyingBaskets: count,
    identifiedBaskets: count,
    anonymousBaskets: count,
    observedBuyers: count,
    knownTypeBuyers: count,
    noKnownTypeBuyers: count,
    mixedTypeBuyers: count,
    knownTypeLines: count,
    unknownTypeLines: count,
    knownDates: count,
    unknownDates: count,
    totalTypes: count,
    ordersSyncedAt: date,
    linesSyncedAt: date,
    observedStart: date,
    observedEnd: date,
});
const category = z.strictObject({
    label: z.string().regex(/[^\s\p{Z}\uFEFF\u0085]/u),
    buyers: count.positive(),
});
export const productMixInputsSchema = z
    .strictObject({
    coverage: productMixCoverageSchema,
    categories: z.array(category),
})
    .superRefine(({ coverage: c, categories }, ctx) => {
    const valid = c.totalOrders === c.eligibleOrders + c.excludedOrders + c.unknownOrders &&
        c.currentLines >= c.eligibleOrderLines &&
        c.eligibleOrderLines === c.eligibleLines + c.excludedLines + c.unknownLines &&
        c.eligibleOrdersWithoutLines <= c.eligibleOrders &&
        c.qualifyingBaskets <= c.eligibleOrders - c.eligibleOrdersWithoutLines &&
        c.eligibleOrders - c.eligibleOrdersWithoutLines <= c.eligibleOrderLines &&
        c.qualifyingBaskets <= c.eligibleLines &&
        (c.qualifyingBaskets === 0) === (c.eligibleLines === 0) &&
        c.identifiedBaskets + c.anonymousBaskets === c.qualifyingBaskets &&
        c.observedBuyers <= c.identifiedBaskets &&
        (c.observedBuyers === 0) === (c.identifiedBaskets === 0) &&
        c.knownTypeBuyers + c.noKnownTypeBuyers === c.observedBuyers &&
        c.mixedTypeBuyers <= c.knownTypeBuyers &&
        c.knownTypeLines + c.unknownTypeLines === c.eligibleLines &&
        c.knownTypeBuyers <= c.knownTypeLines &&
        c.noKnownTypeBuyers + c.mixedTypeBuyers <= c.unknownTypeLines &&
        c.knownDates + c.unknownDates === c.qualifyingBaskets &&
        (c.totalOrders === 0) === (c.ordersSyncedAt === null) &&
        (c.currentLines === 0) === (c.linesSyncedAt === null) &&
        (c.knownDates === 0) === (c.observedStart === null && c.observedEnd === null) &&
        (c.knownDates === 0 ||
            (c.observedStart !== null && c.observedEnd !== null && c.observedStart <= c.observedEnd)) &&
        c.totalTypes === categories.length &&
        new Set(categories.map((x) => x.label)).size === categories.length &&
        (c.knownTypeBuyers === 0) === (categories.length === 0) &&
        categories.every((x, i) => x.buyers <= c.knownTypeBuyers &&
            (i === 0 ||
                categories[i - 1].buyers > x.buyers ||
                (categories[i - 1].buyers === x.buyers && categories[i - 1].label < x.label))) &&
        categories.reduce((sum, x) => sum + x.buyers, 0) >= c.knownTypeBuyers &&
        categories.reduce((sum, x) => sum + x.buyers, 0) <= c.knownTypeLines &&
        categories.reduce((sum, x) => sum + x.buyers, 0) + c.noKnownTypeBuyers + c.mixedTypeBuyers + c.anonymousBaskets <=
            c.eligibleLines;
    if (!valid)
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: paid product-type populations, dates or ordering',
        });
});
export function productMixFacts(input) {
    const { coverage, categories } = productMixInputsSchema.parse(input);
    let reason = null;
    if (coverage.totalOrders === 0)
        reason = 'no_store_data';
    else if (coverage.qualifyingBaskets === 0 && coverage.eligibleOrdersWithoutLines > 0)
        reason = 'source_incomplete';
    else if (coverage.qualifyingBaskets === 0 && coverage.unknownOrders + coverage.unknownLines > 0)
        reason = 'eligibility_unknown';
    return {
        state: reason === null ? 'ready' : 'unavailable',
        reason,
        coverage,
        categories: categories.map((c) => ({ ...c, share: c.buyers / coverage.observedBuyers })),
    };
}
export const productMixAnswerSchema = z
    .strictObject({
    ...insightAnswerMetadataSchema.shape,
    definition: z.strictObject({ id: z.literal(productMixDefinition.id) }),
    state: z.enum(['ready', 'unavailable']),
    reason: z.enum(['no_store_data', 'source_incomplete', 'eligibility_unknown']).nullable(),
    coverage: productMixCoverageSchema,
    categories: z.array(category.extend({ share: z.number().finite().min(0).max(1) })),
    presentation: insightCategoryBreakdownSchema,
    actions: z.tuple([]),
})
    .superRefine((answer, ctx) => {
    const inputs = productMixInputsSchema.safeParse({
        coverage: answer.coverage,
        categories: answer.categories.map(({ label, buyers }) => ({ label, buyers })),
    });
    if (!inputs.success) {
        ctx.addIssue({ code: 'custom', message: 'INSIGHT_PRESENTATION_INVALID: paid product-type inputs' });
        return;
    }
    const facts = productMixFacts(inputs.data);
    if (answer.evidence.basis !== productMixDefinition.basis ||
        JSON.stringify(answer.evidence.sourceTimestamps) !==
            JSON.stringify([
                { source: 'orders', syncedAt: answer.coverage.ordersSyncedAt },
                { source: 'order_lines', syncedAt: answer.coverage.linesSyncedAt },
            ]) ||
        JSON.stringify(facts) !==
            JSON.stringify({
                state: answer.state,
                reason: answer.reason,
                coverage: answer.coverage,
                categories: answer.categories,
            }) ||
        JSON.stringify(answer.presentation) !== JSON.stringify(buildProductMixPresentation(facts)))
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: paid product-type answer differs from facts',
        });
});
//# sourceMappingURL=product-mix-answer.js.map